import "../index.css";
import { PencilIcon } from "lucide-react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { ImageGallery } from "@/components/ImageGallery";
import type { IContractingCompany } from "@/types/companies-contractors";
import type { ILender } from "@/types/companies-lenders";
import {
  Card,
  Column,
  Heading,
  IconButton,
  LabelValue,
  Row,
  Tooltip,
} from "@/ui-kit";
import { AddressUtils } from "@/utils/address";
import { PhoneUtils } from "@/utils/phone";

type IProps = {
  company: ILender | IContractingCompany | undefined;
  banner?: ReactNode;
  onEdit?: () => void;
};

export const CompanyDetailsCard = ({ company, banner, onEdit }: IProps) => {
  const { t } = useTranslation();
  const labelWidth = "60px";

  return (
    <Card className="profile-card">
      <Card.Body>
        <Column gap="xl" fullWidth>
          {banner}
          <Row gap="xl" alignmentVertical="top">
            <div className="profile-card__image-gallery">
              <ImageGallery imageUrls={company ? [company.companyLogo] : []} />
            </div>
            <Column gap="sm" fullWidth>
              <Row alignmentHorizontal="justify">
                <Heading level={3} tone="default">
                  {t("label.company")}
                </Heading>
                {onEdit && (
                  <div className="profile-card__actions">
                    <Tooltip content={t("action.edit")} placement="bottom-end">
                      <IconButton
                        size="sm"
                        variant="ghost"
                        color="neutral"
                        content={<PencilIcon />}
                        onClick={onEdit}
                        aria-label={t("action.edit")}
                      />
                    </Tooltip>
                  </div>
                )}
              </Row>

              <Column gap="2xs">
                <LabelValue
                  label={t("label.name")}
                  value={company?.name}
                  labelWidth={labelWidth}
                />
                <LabelValue
                  label={t("label.address")}
                  value={AddressUtils.format(company?.address)}
                  labelWidth={labelWidth}
                  canCopy
                />
                <LabelValue
                  label={t("label.phone")}
                  value={PhoneUtils.format(company?.phone)}
                  labelWidth={labelWidth}
                  canCopy
                />
                <LabelValue
                  label={t("label.email")}
                  value={company?.email}
                  labelWidth={labelWidth}
                  canCopy
                />
              </Column>
            </Column>
          </Row>
        </Column>
      </Card.Body>
    </Card>
  );
};
